import { useEffect, useMemo, useState } from "react";
import { ArrowRight, ChevronDown, ChevronRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatCisloBezNul, formatEur, formatPct } from "@/lib/format";
import { cn } from "@/lib/utils";
import {
  defaultVyber,
  porovnaj,
  type SucetRozdiel,
  type ZostavaRozdiel,
} from "@/lib/porovnanieVypoctov";
import type { Varianta } from "@/api/schemas";

/** Popisky stavu rozdielu — user nemá čítať interné kľúče. */
const STAV_SK: Record<string, string> = {
  len_v_a: "len v A",
  len_v_b: "len v B",
  zmenene: "zmenené",
  rovnake: "bez zmeny",
};

function nazovVypoctu(v: Varianta): string {
  return v.je_vyrobna ? `${v.nazov} (výrobná)` : v.nazov;
}

/**
 * Porovnanie dvoch výpočtov zákazky.
 *
 * Nepotrebuje žiadny dotaz — `GET /api/zakazky/{id}` vracia všetky
 * varianty naraz s cenami aj celou hierarchiou, takže oba výpočty už
 * v pamäti sú a rozdiel je čistý výpočet (`lib/porovnanieVypoctov.ts`).
 */
export function PorovnanieDialog({
  open,
  onOpenChange,
  varianty,
  aktivnaVariantaId,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  varianty: Varianta[];
  aktivnaVariantaId: string | null | undefined;
}): JSX.Element {
  const [aId, setAId] = useState<string | null>(null);
  const [bId, setBId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const d = defaultVyber(varianty, aktivnaVariantaId);
    setAId(d.aId);
    setBId(d.bId);
    // Default sa nastavuje pri každom otvorení — medzi otvoreniami mohla
    // pribudnúť výrobná varianta a tá je najčastejšia otázka
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const a = varianty.find((v) => v.id === aId);
  const b = varianty.find((v) => v.id === bId);
  const porovnanie = useMemo(() => (a && b ? porovnaj(a, b) : null), [a, b]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>Porovnanie výpočtov</DialogTitle>
          <DialogDescription className="sr-only">
            Rozdiely medzi dvomi výpočtami — súčty, zostavy a dielce.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-end gap-3">
          <div className="space-y-1">
            <Label htmlFor="por-a" className="text-xs">
              Výpočet A
            </Label>
            <select
              id="por-a"
              value={aId ?? ""}
              onChange={(e) => setAId(e.target.value)}
              className="h-9 w-56 rounded-md border bg-background px-2 text-sm"
            >
              {varianty.map((v) => (
                <option key={v.id} value={v.id}>
                  {nazovVypoctu(v)}
                </option>
              ))}
            </select>
          </div>
          <ArrowRight className="mb-2 h-4 w-4 shrink-0 text-muted-foreground" />
          <div className="space-y-1">
            <Label htmlFor="por-b" className="text-xs">
              Výpočet B
            </Label>
            <select
              id="por-b"
              value={bId ?? ""}
              onChange={(e) => setBId(e.target.value)}
              className="h-9 w-56 rounded-md border bg-background px-2 text-sm"
            >
              {varianty.map((v) => (
                <option key={v.id} value={v.id}>
                  {nazovVypoctu(v)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {!porovnanie ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Vyber dva výpočty na porovnanie.
          </p>
        ) : aId === bId ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            A aj B je ten istý výpočet — vyber dva rôzne.
          </p>
        ) : (
          <div className="max-h-[60vh] space-y-4 overflow-y-auto">
            <SuctyTabulka sucty={porovnanie.sucty} />
            <ZostavyTabulka
              zostavy={porovnanie.zostavy}
              rovnakych={porovnanie.rovnakychZostav}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function formatJednotky(v: number, jednotka: "eur" | "pct" | "koef"): string {
  if (jednotka === "eur") return formatEur(v);
  if (jednotka === "koef") return `×${formatCisloBezNul(v)}`;
  return formatPct(v, 2);
}

/** Rozdiel sa farbí znamienkom, nie len číslom — plus je drahšie. */
function Rozdiel({
  hodnota,
  jednotka,
  pct,
}: {
  hodnota: number;
  jednotka: "eur" | "pct" | "koef";
  pct?: number | null;
}): JSX.Element {
  if (hodnota === 0) {
    return <span className="text-muted-foreground">—</span>;
  }
  const znak = hodnota > 0 ? "+" : "−";
  const abs = Math.abs(hodnota);
  return (
    <span
      className={cn(
        "font-medium",
        hodnota > 0 ? "text-destructive" : "text-emerald-700",
      )}
    >
      {znak}
      {formatJednotky(abs, jednotka)}
      {pct != null && jednotka === "eur" ? (
        <span className="ml-1 text-xs font-normal text-muted-foreground">
          ({znak}
          {formatPct(Math.abs(pct), 1)})
        </span>
      ) : null}
    </span>
  );
}

function SuctyTabulka({ sucty }: { sucty: SucetRozdiel[] }): JSX.Element {
  return (
    <section className="rounded-lg border bg-card">
      <div className="border-b bg-background-alt/60 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        Súčty
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[140px]">Položka</TableHead>
            <TableHead className="text-right">A</TableHead>
            <TableHead className="text-right">B</TableHead>
            <TableHead className="text-right">Rozdiel</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sucty.map((s) => (
            <TableRow key={s.pole} data-testid={`sucet-${s.pole}`}>
              <TableCell className="text-sm">{s.pole}</TableCell>
              <TableCell className="text-right font-mono text-sm">
                {formatJednotky(s.a, s.jednotka)}
              </TableCell>
              <TableCell className="text-right font-mono text-sm">
                {formatJednotky(s.b, s.jednotka)}
              </TableCell>
              <TableCell className="text-right font-mono text-sm">
                <Rozdiel
                  hodnota={s.rozdiel}
                  jednotka={s.jednotka}
                  pct={s.rozdielPct}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </section>
  );
}

function ZostavyTabulka({
  zostavy,
  rovnakych,
}: {
  zostavy: ZostavaRozdiel[];
  rovnakych: number;
}): JSX.Element {
  // Nezmenené zostavy sa skrývajú za jeden riadok — v rozdiele má byť
  // vidieť to, čo sa mení, nie 30 riadkov „bez zmeny"
  const [ukazatRovnake, setUkazatRovnake] = useState(false);
  const zmenene = zostavy.filter((z) => z.stav !== "rovnake");
  const viditelne = ukazatRovnake ? zostavy : zmenene;

  return (
    <section className="rounded-lg border bg-card">
      <div className="border-b bg-background-alt/60 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        Zostavy
      </div>
      {zmenene.length === 0 && !ukazatRovnake ? (
        <p className="px-3 py-4 text-sm text-muted-foreground">
          Zostavy sú v oboch výpočtoch rovnaké.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead className="w-[180px]">Zostava</TableHead>
              <TableHead className="w-[110px]">Stav</TableHead>
              <TableHead className="text-right">ks A / B</TableHead>
              <TableHead className="text-right">Cena A</TableHead>
              <TableHead className="text-right">Cena B</TableHead>
              <TableHead className="text-right">Rozdiel</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {viditelne.map((z) => (
              <ZostavaRiadok key={z.kod} z={z} />
            ))}
          </TableBody>
        </Table>
      )}
      {rovnakych > 0 ? (
        <button
          type="button"
          onClick={() => setUkazatRovnake((v) => !v)}
          className="w-full border-t px-3 py-2 text-left text-xs text-muted-foreground hover:bg-background-alt/40"
        >
          {ukazatRovnake
            ? "Skryť zostavy bez zmeny"
            : `${rovnakych} ${rovnakych === 1 ? "zostava" : "zostáv"} bez zmeny — zobraziť`}
        </button>
      ) : null}
    </section>
  );
}

function ZostavaRiadok({ z }: { z: ZostavaRozdiel }): JSX.Element {
  const [otvorene, setOtvorene] = useState(false);
  const zmeneneDielce = z.dielce.filter((d) => d.stav !== "rovnake");
  const maDetail = zmeneneDielce.length > 0;

  return (
    <>
      <TableRow
        className={maDetail ? "cursor-pointer" : undefined}
        onClick={maDetail ? () => setOtvorene((o) => !o) : undefined}
        data-testid={`zostava-${z.kod}`}
      >
        <TableCell className="p-1 text-center">
          {maDetail ? (
            otvorene ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )
          ) : null}
        </TableCell>
        <TableCell className="text-sm">
          <span className="font-mono">{z.kod}</span>
          {/* Spárované podľa názvu — ID sa medzi výpočtami líšia
              (prečíslovanie, KOLO 97). Bez tohto by sa zdalo, že
              porovnanie páruje niečo, čo nesedí. */}
          {z.kodB ? (
            <span
              className="ml-1 font-mono text-muted-foreground"
              title="Spárované podľa názvu — ID sa v druhom výpočte líši"
            >
              ↔ {z.kodB}
            </span>
          ) : null}
          <span className="ml-2 text-muted-foreground">{z.nazov}</span>
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">
          {STAV_SK[z.stav] ?? z.stav}
          {maDetail ? (
            <span className="ml-1">
              ({zmeneneDielce.length}{" "}
              {zmeneneDielce.length === 1 ? "dielec" : "dielcov"})
            </span>
          ) : null}
        </TableCell>
        <TableCell className="text-right font-mono text-sm">
          {z.pocetKsA ?? "—"} / {z.pocetKsB ?? "—"}
        </TableCell>
        <TableCell className="text-right font-mono text-sm">
          {z.cenaA == null ? "—" : formatEur(z.cenaA)}
        </TableCell>
        <TableCell className="text-right font-mono text-sm">
          {z.cenaB == null ? "—" : formatEur(z.cenaB)}
        </TableCell>
        <TableCell className="text-right font-mono text-sm">
          <Rozdiel hodnota={z.cenaRozdiel} jednotka="eur" />
        </TableCell>
      </TableRow>
      {otvorene ? (
        <TableRow>
          <TableCell colSpan={7} className="bg-background-alt/40 p-0">
            <Table>
              <TableBody>
                {zmeneneDielce.map((d) => (
                  <TableRow key={d.kod}>
                    <TableCell className="w-[200px] font-mono text-xs">
                      {d.kod}
                      {d.kodB ? (
                        <span
                          className="ml-1 text-muted-foreground"
                          title="Spárované podľa názvu — ID sa v druhom výpočte líši"
                        >
                          ↔ {d.kodB}
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell className="w-[110px] text-xs text-muted-foreground">
                      {STAV_SK[d.stav] ?? d.stav}
                    </TableCell>
                    <TableCell className="text-xs">
                      {d.zmenenePolia.length
                        ? d.zmenenePolia.join(", ")
                        : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}
